import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';
import ReBarChart from 'switch-fe-shared/components/Charts/ReBarChart';
import { formatNumber } from 'switch-shared/utils/formatters';

import {
  formatPercent,
  getSolarChartColors,
  monthKeyLabel,
  performanceRatio,
} from '../../utils/solarPresentation';
import SolarCardState, { type SolarCardStatus } from './SolarCardState';

interface Props {
  status: SolarCardStatus;

  title: string;

  avatar?: JSX.Element;

  /** Months as `YYYY-MM`, oldest first. */
  keys: string[];

  actual: number[];

  /** Whether each month has solar data; months without show no performance. */
  hasData: boolean[];

  /** Absent when the site has no forecast meter point. */
  forecast?: number[];

  unit: string;

  actualName: string;

  forecastName: string;

  /** Decimal places of the values drawn on the bars. */
  decimals: number;
}

/** Monthly actual bars against forecast bars, with each month's performance against forecast under its label. */
const SolarForecastBarsCard = ({
  status,
  title,
  avatar,
  keys,
  actual,
  hasData,
  forecast,
  unit,
  actualName,
  forecastName,
  decimals,
}: Props) => {
  const theme = useTheme();
  const colors = getSolarChartColors(theme);

  const subLabels = useMemo(
    () =>
      forecast &&
      actual.map((value, i) => {
        const ratio = hasData[i] ? performanceRatio(value, forecast[i]) : null;
        return ratio == null ? '—' : formatPercent(ratio);
      }),
    [actual, hasData, forecast]
  );
  const formatValue = (value: number) => formatNumber(value, decimals);

  return (
    <WidgetCard
      title={title}
      subheader={
        forecast
          ? `${unit} | % under each month is performance against forecast`
          : `${unit} | No forecast meter point`
      }
      avatar={avatar}
      cardContentProps={{
        sx: { display: 'flex', flexDirection: 'column', pb: '16px !important' },
      }}
    >
      <SolarCardState status={status}>
        <Box flex={1} minHeight={0}>
          <ReBarChart
            categories={keys.map(monthKeyLabel)}
            series={[
              ...(forecast ? [{ name: forecastName, data: forecast }] : []),
              { name: actualName, data: actual },
            ]}
            colors={
              forecast ? [colors.forecast, colors.actual] : [colors.actual]
            }
            barOpacity={1}
            showLegend
            valueLabels={{ vertical: true, formatter: formatValue }}
            xSubLabels={subLabels}
            yFormatter={formatValue}
            unit={unit}
            fileName={title}
          />
        </Box>
      </SolarCardState>
    </WidgetCard>
  );
};

export default SolarForecastBarsCard;
